import React, { Dispatch, SetStateAction } from "react";
import {
  ApprovalLinkData,
  Ev2ExperimentStage,
  ExperimentType,
  PipelineRun,
} from "../../../Types";
import {
  DetailsList,
  DetailsListLayoutMode,
  MessageBar,
  MessageBarType,
  SelectionMode,
  Spinner,
} from "@fluentui/react";
import { styles } from "./rolloutDetailsTable.styles";
import { styles as sharedStyles } from "../../../Shared/shared.styles";
import { getPipelineRunsColumns } from "./rolloutDetailsColumnDefinitions";
import RolloutDetailsEv2Panel from "./RolloutDetailsEv2Panel/RolloutDetailsEv2Panel";
import ConfigurePipelineOverview from "./ConfigurePipeline/ConfigurePipelineOverview";
import useAppInsights from "../../../Hooks/UseAppInsights";

interface RolloutDetailsTableProps {
  /**
   * The pipeline ID associated with the runs
   */
  pipelineId?: number | null;
  /**
   * When true, indicates that the pipeline ID is still loading
   */
  pipelineIdLoading?: boolean;
  /**
   * Array of pipeline runs to display in the table
   */
  pipelineRuns: PipelineRun[];
  /**
   * When true, shows loading state for pipeline runs
   */
  loadingRuns: boolean;
  /**
   * Mapping of pipeline run IDs to their approval link data, used to render approval status and links in the table
   */
  approvalData: Record<number, ApprovalLinkData | null>;
  /**
   * Mapping of pipeline run IDs to their loading state for approval link data, used to show loading indicators for approval status in the table
   */
  loadingApprovals: Record<number, boolean>;
  /**
   * Mapping of pipeline run IDs to their EV2 portal links, used to render links to EV2 experiment details in the table
   */
  ev2PortalLinks: Record<number, string | null>;
  /**
   * Mapping of pipeline run IDs to their loading state for EV2 portal links, used to show loading indicators for EV2 link data in the table
   */
  loadingEv2Links: Record<number, boolean>;
  /**
   * Mapping of pipeline run IDs to their EV2 experiment stage data, used to render EV2 experiment status in the table
   */
  ev2StatusMap: Record<number, Ev2ExperimentStage[]>;
  /**
   * Mapping of pipeline run IDs to their loading state for EV2 experiment stage data, used to show loading indicators for EV2 status in the table
   */
  ev2StatusLoading: Record<number, boolean>;
  /**
   * Error if pipeline runs or related data failed to load
   */
  runsError: Error | string | null;
  /**
   * The experiment configuration data
   */
  experimentDetails: ExperimentType;
  /**
   * Setter function for updating the stages loading state in parent component
   */
  setStagesLoading: Dispatch<SetStateAction<boolean>>;
}

/**
 * Table component displaying pipeline runs with status, approval links,
 * and EV2 integration for experiment rollout monitoring and management.
 */
const RolloutDetailsTable: React.FC<RolloutDetailsTableProps> = ({
  pipelineId,
  pipelineIdLoading,
  pipelineRuns = [],
  loadingRuns,
  approvalData,
  loadingApprovals,
  ev2PortalLinks,
  loadingEv2Links,
  ev2StatusMap,
  ev2StatusLoading,
  runsError,
  experimentDetails,
  setStagesLoading,
}) => {
  const [showRolloutDetailsEv2Panel, setShowRolloutDetailsEv2Panel] =
    React.useState(false);
  const [ev2Stages, setEv2Stages] = React.useState<Ev2ExperimentStage[]>([]);
  const [ev2BaseLink, setEv2BaseLink] = React.useState<string>("");
  const abnVariantCount = experimentDetails?.variantDetails?.length;

  const { trackOperationEvent } = useAppInsights("RolloutDetailsTable");

  React.useEffect(() => {
    const isLoading = Object.values(ev2StatusLoading).some(
      (loading) => loading,
    );
    setStagesLoading(isLoading);
  }, [ev2StatusLoading, setStagesLoading]);

  const onEv2StageClicked = (runId: number) => {
    setShowRolloutDetailsEv2Panel(true);
    if (ev2StatusMap && ev2StatusMap[runId]) {
      setEv2Stages(ev2StatusMap[runId]);
    }
    if (ev2PortalLinks && ev2PortalLinks[runId]) {
      setEv2BaseLink(ev2PortalLinks[runId]!);
    }
    trackOperationEvent({
      operation: "OpenRolloutDetailsEv2Panel",
      function: "onEv2StageClicked",
      properties: { runId, panelComponent: "RolloutDetailsEv2Panel" },
    });
  };

  const pipelineRunsColumns = getPipelineRunsColumns({
    loadingApprovals,
    approvalData,
    loadingEv2Links,
    ev2PortalLinks,
    ev2StatusMap,
    ev2StatusLoading,
    onEv2StageClicked,
    abnVariantCount,
  });

  if (experimentDetails.prUrl && pipelineRuns.length === 0) {
    return (
      <div className={styles.rolloutDetailsReview}>
        <span role="status">Experiment currently in review</span>
      </div>
    );
  }

  return (
    <>
      <div className={sharedStyles.scrollbarThin}>
        {loadingRuns || pipelineIdLoading ? (
          <div className={styles.rolloutDetailsTableLoading}>
            <Spinner label="Loading pipeline runs..." />
          </div>
        ) : !pipelineId ? (
          <ConfigurePipelineOverview />
        ) : pipelineRuns.length === 0 ? (
          <div className={styles.rolloutDetailsTableEmpty}>
            No pipeline runs found for this configuration.
          </div>
        ) : (
          <div>
            {runsError && (
              <MessageBar messageBarType={MessageBarType.error}>
                {runsError instanceof Error
                  ? runsError.message
                  : String(runsError)}
              </MessageBar>
            )}
            <DetailsList
              items={pipelineRuns}
              columns={pipelineRunsColumns}
              selectionMode={SelectionMode.none}
              className={styles.rolloutDetailsTable}
              layoutMode={DetailsListLayoutMode.justified}
            />
          </div>
        )}
      </div>
      {showRolloutDetailsEv2Panel && (
        <RolloutDetailsEv2Panel
          ev2Stages={ev2Stages}
          ev2BaseLink={ev2BaseLink}
          abnVariantCount={experimentDetails?.variantDetails?.length}
          onCancel={() => {
            setShowRolloutDetailsEv2Panel(false);
            trackOperationEvent({
              operation: "Cancel",
              function: "onCancel",
              properties: { panelComponent: "RolloutDetailsEv2Panel" },
            });
          }}
        />
      )}
    </>
  );
};

export default RolloutDetailsTable;
